import _ from "lodash";
import client from "lib/apolloClient/client";
import update from "immutability-helper";
import moment from "moment";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";
import { generateRandomId } from "Utils";
import { getDefaultOrderBy } from "Utils/GetFormattedUserName";

import {
  createMessageMutation,
  deleteMessageMutation,
  updateMessageMutation,
} from "modules/CommonMutations";
import {
  writeCommentsOfNodeQuery,
  writeStudentGoalsToCache,
  getCommentsOfNodeFromCache,
} from "./RightPaneGraphqlHelpers";
import {
  addEvidencesForStudentGoalsMutation,
  createPostMutation,
  markCommentsReadForFieldMutation,
  updateGoalTaskMutation,
} from "./RightPaneMutations";
import { getCommentOfNodeQuery } from "./RightPaneQueries";

import { getStudentGoalFeedFromCache } from "Teacher/routes/StudentGoals/components/GoalList/modules/GoalListGraphqlHelpers";
import { getStudentGoalsQuery } from "Teacher/routes/StudentGoals/components/GoalList/modules/GoalListQueries";
import {
  getOrganizationId,
  getUserId,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { getStudentByCourseIdsQuery } from "Teacher/routes/StudentGoals/components/TeacherView/components/LeftPane/components/StudentList/modules/StudentListQueries";
import { TAB_NAMES } from "Teacher/routes/StudentGoals/components/GoalAccordion/modules/constants";
import { getSelectedGoalItem } from "./RightPaneUtils";

const updateGoalTaskDataInCache = (
  id,
  filters,
  goalTaskProperties,
  studentId
) => {
  const cachedStudentGoals = getStudentGoalFeedFromCache({
    studentId,
    filters,
  });
  const { studentGoalsV2: { edges, count = {} } = {} } = cachedStudentGoals;

  const studentGoalIndex = edges.findIndex(edge => {
    const { goalTasks } = edge.node;
    return goalTasks.findIndex(task => task.id === id) !== -1;
  });

  if (_.isEqual(studentGoalIndex, -1)) {
    return;
  }

  const { edges: goalEdges } = cachedStudentGoals.studentGoalsV2;
  const goalTaskIndex = goalEdges[studentGoalIndex].node.goalTasks.findIndex(
    task => task.id === id
  );

  const updatedStudentGoals = update(cachedStudentGoals, {
    studentGoalsV2: {
      edges: {
        [studentGoalIndex]: {
          node: {
            goalTasks: {
              [goalTaskIndex]: {
                $merge: { id, ...goalTaskProperties },
              },
            },
          },
        },
      },
    },
  });

  _.delay(() => {
    const isComplete = updatedStudentGoals.studentGoalsV2.edges[
      studentGoalIndex
    ].node.goalTasks.every(task => task.isComplete);
    if (
      (filters.status === TAB_NAMES.ONGOING && isComplete) ||
      (filters.status === TAB_NAMES.COMPLETED && !isComplete)
    ) {
      updatedStudentGoals.studentGoalsV2.edges.splice(studentGoalIndex, 1);
      updatedStudentGoals.studentGoalsV2.count = {
        ongoing: (count.ongoing || 0) + (isComplete ? -1 : 1),
        completed: (count.completed || 0) + (isComplete ? +1 : -1),
        __typename: "StudentGoalCount",
      };
      writeStudentGoalsToCache({
        studentId,
        filters,
        data: updatedStudentGoals,
      });
    }
  }, 1000);
  writeStudentGoalsToCache({ studentId, filters, data: updatedStudentGoals });
};

export const createPostForAttachments = postItem => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const userId = getUserId(state);
      const { attachments } = postItem;
      const newAttachments = _.map(attachments, item => {
        if (_.includes(item?.id, "NEW_")) return _.omit(item, "id");
        else return item;
      });

      const variables = {
        attachments: newAttachments,
        createdBy: userId,
        publishedBy: userId,
        state: "PUBLISHED",
      };

      const res = await client.mutate({
        mutation: createPostMutation,
        variables,
      });
      return res;
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const updateGoalTask = goalTask => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const userId = getUserId(state);
      const { studentId, selectedTab, searchText } = state.studentGoals;
      const { id, ...restGoalTask } = goalTask;
      const input = {
        ...goalTask,
        updatedBy: userId,
      };

      const filters = {
        status: selectedTab,
        searchText,
      };

      updateGoalTaskDataInCache(id, filters, restGoalTask, studentId);

      const res = await client.mutate({
        mutation: updateGoalTaskMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
      });
      return res;
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const addEvidencesForStudentGoals = ({ parentId, evidenceIds }) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const { studentId, selectedTab, searchText } = state.studentGoals;
      const filters = {
        status: selectedTab,
        searchText,
      };

      const input = {
        parentId,
        parentType: "STUDENT_GOAL",
        evidenceType: "POST",
        evidenceIds,
      };

      const res = await client.mutate({
        mutation: addEvidencesForStudentGoalsMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: false,
        refetchQueries: [
          {
            query: getStudentGoalsQuery,
            variables: {
              studentId,
              filters,
            },
          },
        ],
      });
      return res;
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

const getOptimisticResponseObject = ({ state, label, attachments }) => ({
  __typename: "Mutation",
  platform: {
    __typename: "PlatformMutations",
    createMessage: {
      id: generateRandomId(),
      isCommentRead: true,
      label: label,
      itemType: "ATTACHMENTS",
      itemTypeV2: "ATTACHMENTS",
      metadata: null,
      type: "NORMAL",
      item: {
        id: generateRandomId(),
        attachments: attachments,
        __typename: "AttachmentSet",
      },
      createdAt: moment().toISOString(),
      createdBy: {
        ...getUserBasicDetailsFromCache({
          id: _.get(state, "login.userInfo.id"),
          type: _.get(state, "login.userInfo.userEntityType"),
        }),
        type: _.get(state, "login.userInfo.user_type"),
      },
      isDeleted: false,
      __typename: "Message",
    },
  },
});

export const createComment = commentData => {
  return async (dispatch, getState) => {
    const {
      parentId,
      parentType,
      subEntityType,
      attachments,
      label,
      isCommunity,
    } = commentData;
    const state = getState();
    const currentComments = getCommentsOfNodeFromCache({
      id: parentId,
      type: parentType,
      isCommunity,
    });

    const commentsKey =
      parentType === "STUDENT_GOAL" ? "studentGoalComments" : "comments";
    try {
      await client.mutate({
        mutation: createMessageMutation,
        variables: {
          parentId: parentId,
          parentType: parentType,
          subEntityType: subEntityType,
          attachments: _.map(attachments, attachment =>
            _.omit(attachment, ATTACHMENT_OMIT_ATTRIBUTES)
          ),
          label: label,
        },
        optimisticResponse: getOptimisticResponseObject({
          state,
          label,
          attachments,
        }),
        update: (cache, { data }) => {
          const {
            platform: { createMessage },
          } = data;
          const comments = _.get(
            currentComments,
            `${
              parentType == "STUDENT_GOAL" ? "studentGoalComments" : "comments"
            }.edges`,
            []
          );
          const updatedComments = [
            {
              node: createMessage,
              __typename: "ConversationMessageEdge",
            },
            ...comments,
          ];
          const commentsObject = {
            ..._.get(currentComments, commentsKey, {}),
            edges: updatedComments,
            totalCount:
              _.get(currentComments, `${commentsKey}.totalCount`, 0) + 1,
          };
          setTimeout(() => {
            const variables = {
              id: parentId,
              type: parentType,
              isCommunity,
            };

            const commentsData = {
              ...currentComments,
              [commentsKey]: commentsObject,
              __typename:
                parentType == "STUDENT_GOAL" ? "StudentGoal" : "GoalTask",
            };

            writeCommentsOfNodeQuery({
              variables,
              data: commentsData,
            });
          });
        },
      });
    } catch (e) {
      setTimeout(() => {
        const variables = {
          id: parentId,
          type: parentType,
          isCommunity,
        };
        writeCommentsOfNodeQuery({
          variables,
          data: {
            ...currentComments,
            [commentsKey]: {
              ..._.get(currentComments, `${commentsKey}`, {}),
            },
            __typename:
              parentType == "STUDENT_GOAL" ? "StudentGoal" : "GoalTask",
          },
        });
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteComment = commentData => {
  return async dispatch => {
    const commentsKey =
      parentType === "STUDENT_GOAL" ? "studentGoalComments" : "comments";
    const { parentId, commentId, parentType, isCommunity } = commentData;
    const currentComments = getCommentsOfNodeFromCache({
      id: parentId,
      type: parentType,
      isCommunity,
    });
    try {
      const result = await client.mutate({
        mutation: deleteMessageMutation,
        variables: {
          id: commentId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            deleteMessage: {
              id: generateRandomId(),
              isDeleted: true,
              __typename: "Message",
            },
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getCommentOfNodeQuery,
            variables: {
              id: parentId,
              type: parentType,
              isCommunity,
            },
          },
        ],
        update: () => {
          const updatedComments = _.filter(
            _.get(currentComments, `${commentsKey}.edges`),
            comment => _.get(comment, "node.id") != commentId
          );
          const comments = _.get(currentComments, commentsKey);
          const commentsOfPost = {
            ...comments,
            edges: updatedComments,
            totalCount: updatedComments.length,
          };
          setTimeout(() => {
            const variables = {
              id: parentId,
              type: parentType,
              isCommunity,
            };

            const commentsData = {
              ...currentComments,
              [commentsKey]: commentsOfPost,
            };

            writeCommentsOfNodeQuery({
              variables,
              data: commentsData,
            });
          });
        },
      });
      if (_.get(result, "data.platform.deleteMessage", false) == true) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:comment_successfully_deleted",
            type: "success",
            position: "toast-bottom-left",
          })
        );
      }
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateComment = commentData => {
  return async (dispatch, getState) => {
    const commentsKey =
      parentType === "STUDENT_GOAL" ? "studentGoalComments" : "comments";
    const {
      id,
      attachments,
      label,
      parentId,
      parentType,
      isCommunity,
    } = commentData;

    const currentComments = getCommentsOfNodeFromCache({
      id: parentId,
      type: parentType,
      isCommunity,
    });
    const state = getState();
    try {
      await client.mutate({
        mutation: updateMessageMutation,
        variables: {
          id: id,
          attachments: _.map(attachments, attachment =>
            _.omit(attachment, ATTACHMENT_OMIT_ATTRIBUTES)
          ),
          label: label,
        },
        optimisticResponse: getOptimisticResponseObject({
          state,
          attachments,
          label,
        }),
        update: (cache, { data }) => {
          const {
            platform: { updateMessage },
          } = data;
          const updatedComments = [
            ..._.get(currentComments, `${commentsKey}.edges`, []),
          ];
          const index = _.findIndex(
            updatedComments,
            comment => _.get(comment, "node.id") == id
          );
          if (index < 0) return;

          updatedComments[index] = {
            node: updateMessage,
            __typename: "ConversationMessageEdge",
          };
          const commentsObject = {
            ..._.get(currentComments, commentsKey, {}),
            edges: updatedComments,
          };
          setTimeout(() => {
            const variables = {
              id: parentId,
              type: parentType,
              isCommunity,
            };
            writeCommentsOfNodeQuery({
              variables,
              data: {
                ...currentComments,
                [commentsKey]: commentsObject,
              },
            });
          });
        },
      });
    } catch (e) {
      setTimeout(() => {
        const variables = {
          id: parentId,
          type: parentType,
          isCommunity,
        };
        writeCommentsOfNodeQuery({
          variables,
          data: {
            ...currentComments,
            [commentsKey]: {
              ..._.get(currentComments, commentsKey, {}),
            },
          },
        });
      });
    }
  };
};

export const getSelectedRow = (selectedItem, selectedItemId) => {
  const { id, goalTasks } = selectedItem;
  if (selectedItemId === id) {
    return selectedItem;
  }
  return _.find(goalTasks, { id: selectedItemId });
};

const getPreviousPostIds = selectedRow => {
  if (_.has(selectedRow, "goalTasks")) {
    return _.map(_.get(selectedRow, "evidences.edges", []), "evidenceItem.id");
  } else {
    return _.map(selectedRow?.posts, "id");
  }
};

export const handlePostCreationForStudentGoal = ({
  parentType,
  attachments,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      selectedItemId,
      studentId,
      searchText,
      selectedTab,
    } = state.studentGoals;

    const studentGoalsDataFromCache = getStudentGoalFeedFromCache({
      studentId,
      filters: {
        status: selectedTab,
        searchText,
      },
    });

    const selectedItem = getSelectedGoalItem(
      studentGoalsDataFromCache,
      selectedItemId
    );
    const selectedRow = getSelectedRow(selectedItem, selectedItemId);
    const postResponse = await dispatch(
      createPostForAttachments({ attachments })
    );
    const postId = _.get(postResponse, "data.documentation.createPost.id");
    const parentId = selectedRow.id;
    if (parentType === "STUDENT_GOAL") {
      await dispatch(
        addEvidencesForStudentGoals({
          parentId,
          evidenceIds: [postId],
        })
      );
    } else {
      await dispatch(
        updateGoalTask({
          id: parentId,
          postIds: [...getPreviousPostIds(selectedRow), postId],
        })
      );
    }
  };
};

export const markCommentsAsRead = ({ id, parentType }) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const orgId = getOrganizationId(state);
      const academicYearId = getSelectedAcademicYearId(state);
      const {
        studentId,
        selectedTab,
        searchText,
        courseFilters,
      } = state.studentGoals;
      const filters = {
        status: selectedTab,
        searchText,
      };

      const input = [
        {
          entityId: id,
          entityType: parentType,
        },
      ];
      const isCourseFilterEmpty = _.isEmpty(
        _.get(courseFilters, "courses", [])
      );

      const refetchQueries = [
        {
          query: getStudentGoalsQuery,
          variables: {
            studentId,
            filters,
          },
        },
      ];

      if (!isCourseFilterEmpty) {
        refetchQueries.push({
          query: getStudentByCourseIdsQuery,
          variables: {
            id: orgId,
            filters: {
              ...courseFilters,
              archivalState: "ACTIVE",
              academicYearIds: _.compact([academicYearId]),
            },
            orderBy: getDefaultOrderBy({ type: "student" }),
            studentCurriculumProgramFilters: {
              academicYearId,
            },
          },
        });
      }

      await client.mutate({
        mutation: markCommentsReadForFieldMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries,
      });
    } catch (error) {}
  };
};
